export function insertInlineFiltersInGrid(
  grid,
  frequency = 5,
  columnsCount = 2,
  startPosition = frequency
) {
  // Insert INLINE_FILTERS rows starting at `startPosition`, then repeating every `frequency` rows
  const newGrid = [...grid];
  let offset = 0;
  let filterIndex = 0;

  for (let i = startPosition; i <= grid.length; i += frequency) {
    const targetRow = i + offset;
    const inlineFiltersRow = Array(columnsCount).fill(`INLINE_FILTERS_${filterIndex}`);

    if (targetRow <= newGrid.length) {
      newGrid.splice(targetRow, 0, inlineFiltersRow);
      offset++;
      filterIndex++;
    }
  }

  return newGrid;
}

export function insertWidgetsInGrid(grid, widgets, columnsCount = 2) {
    // Create a copy of the original grid to avoid modifying it directly
    let newGrid = [...grid];
  
    // Step 1: Create a list of widgets with their original indices
    const widgetsWithIndices = widgets.map((widget, index) => ({
      originalIndex: index, // Store the original index in the widgets array
      position: widget.position,
    }));
  
    // Step 2: Filter out duplicate widgets by position, keeping the first occurrence
    const uniqueWidgets = [];
    const seenPositions = new Set();
  
    // Sort by position to ensure consistent ordering
    widgetsWithIndices.sort((a, b) => a.position - b.position);
  
    // Keep only the first widget for each position
    widgetsWithIndices.forEach(widget => {
      if (!seenPositions.has(widget.position)) {
        seenPositions.add(widget.position);
        uniqueWidgets.push(widget);
      }
    });
  
    // Step 3: Insert the unique widgets into the grid using their original indices
    let offset = 0;
  
    uniqueWidgets.forEach(widget => {
      const { position, originalIndex } = widget;
  
      // Calculate the target row based on position and columnsCount
      let targetRow = Math.floor(position / columnsCount);
  
      // Adjust the target row by the number of widgets already inserted
      targetRow += offset;
  
      // Create the widget row using the original index
      const widgetRow = [`WIDGET_${originalIndex}`, `WIDGET_${originalIndex}`];
  
      // Insert the widget row at the target position
      if (targetRow <= newGrid.length) {
        newGrid.splice(targetRow, 0, widgetRow);
      } else {
        newGrid.push(widgetRow);
      }
  
      // Increment the offset since we’ve added a new row
      offset++;
    });
  
    return newGrid;
}

export const LOW_RESULT_DIVIDER_ROW_HEIGHT = 56;
// mSite in-grid low-result: 8px parent padding + white card (12px padding + text) + 8px parent padding
export const LOW_RESULT_DIVIDER_ROW_HEIGHT_MSITE = 84;

export function isLowResultDividerRow(row) {
  return (
    Array.isArray(row) &&
    row.some(cell => typeof cell === 'string' && cell.includes('LOW_RESULT_MSG'))
  );
}

export function insertLowResultSearchMessageInGrid(grid, exactMatchCount, columnsCount = 2) {
  // Inserts a LOW_RESULT_MSG row after exactly `exactMatchCount` products. Re-flows rows when
  // the boundary falls mid-row or when sentinel rows (widgets, inline filters) are present.
  if (!Array.isArray(grid) || exactMatchCount <= 0 || columnsCount <= 0) {
    return grid;
  }

  const isSentinel = cell =>
    typeof cell === 'string' &&
    (cell.includes('WIDGET') || cell.includes('INLINE_FILTERS') || cell.includes('LOW_RESULT_MSG'));

  const messageRow = Array(columnsCount).fill('LOW_RESULT_MSG');
  const chunkRows = items => {
    const rows = [];
    for (let i = 0; i < items.length; i += columnsCount) {
      rows.push(items.slice(i, i + columnsCount));
    }
    return rows;
  };

  const hasSentinelRow = grid.some(row => Array.isArray(row) && row.some(isSentinel));
  if (!hasSentinelRow) {
    const flat = grid.reduce((acc, row) => acc.concat(row), []);
    if (exactMatchCount >= flat.length) {
      return grid; // every product is an exact match — nothing to divide
    }
    return [
      ...chunkRows(flat.slice(0, exactMatchCount)),
      messageRow,
      ...chunkRows(flat.slice(exactMatchCount))
    ];
  }

  const newGrid = [];
  let productsSeen = 0;
  let inserted = false;
  let expandedProducts = [];
  const flushExpanded = () => {
    chunkRows(expandedProducts).forEach(chunkRow => newGrid.push(chunkRow));
    expandedProducts = [];
  };

  for (let r = 0; r < grid.length; r++) {
    const row = grid[r] || [];
    const rowIsSentinel = row.some(isSentinel);

    if (inserted) {
      if (rowIsSentinel) {
        flushExpanded();
        newGrid.push(row);
      } else {
        expandedProducts = expandedProducts.concat(row.filter(cell => cell && !isSentinel(cell)));
      }
    } else {
      const productCount = rowIsSentinel ? 0 : row.filter(cell => cell && !isSentinel(cell)).length;
      if (productCount === 0) {
        newGrid.push(row);
      } else if (productsSeen + productCount < exactMatchCount) {
        newGrid.push(row);
        productsSeen += productCount;
      } else if (productsSeen + productCount === exactMatchCount) {
        newGrid.push(row);
        newGrid.push(messageRow);
        inserted = true;
      } else {
        const keep = exactMatchCount - productsSeen;
        newGrid.push(row.slice(0, keep));
        newGrid.push(messageRow);
        inserted = true;
        expandedProducts = row.slice(keep).filter(cell => cell && !isSentinel(cell));
      }
    }
  }

  flushExpanded();

  return inserted ? newGrid : grid;
}
